import API from "Config/API";

// Every call the pharmacy console makes, in one place.
//
// Paths only — Config/API owns the host and attaches the admin identity
// headers the backend writes onto audit rows.
const base = (id) => `/admin/pharmacy/${encodeURIComponent(id)}`;

// Strip empty values so `?status=` never reaches the server as a filter for
// the empty string, which would match nothing rather than everything.
const qs = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.append(key, value);
    }
  });
  const str = search.toString();
  return str ? `?${str}` : "";
};

export const listPharmacies = (params) =>
  API.get(`/admin/pharmacy/list${qs(params)}`).then((r) => r.data);

export const getPharmacy = (id) => API.get(base(id)).then((r) => r.data);

export const getStats = (id) => API.get(`${base(id)}/stats`).then((r) => r.data);

export const getOrders = (id, params) =>
  API.get(`${base(id)}/orders${qs(params)}`).then((r) => r.data);

export const getOrderFilters = (id) =>
  API.get(`${base(id)}/orders/filters`).then((r) => r.data);

export const updateProfile = (id, body) =>
  API.patch(`${base(id)}/profile`, body).then((r) => r.data);

export const getBank = (id, { reveal = false, reason } = {}) =>
  API.get(`${base(id)}/bank${qs({ reveal: reveal || undefined, reason })}`).then(
    (r) => r.data
  );

export const updateBank = (id, body) =>
  API.put(`${base(id)}/bank`, body).then((r) => r.data);

export const setVerification = (id, verified, reason) =>
  API.patch(`${base(id)}/verification`, { verified, reason }).then((r) => r.data);

export const setTakingOrders = (id, isTakingOrders, reason) =>
  API.patch(`${base(id)}/taking-orders`, { isTakingOrders, reason }).then(
    (r) => r.data
  );

export const softDeletePharmacy = (id, reason) =>
  API.post(`${base(id)}/soft-delete`, { reason }).then((r) => r.data);

export const restorePharmacy = (id, reason) =>
  API.post(`${base(id)}/restore`, { reason }).then((r) => r.data);

export const placesAutocomplete = (input) =>
  API.get(`/admin/pharmacy/places/autocomplete${qs({ input })}`).then(
    (r) => r.data
  );

export const placeDetails = (placeId) =>
  API.get(`/admin/pharmacy/places/details${qs({ placeId })}`).then((r) => r.data);

export const updateLocation = (id, body) =>
  API.put(`${base(id)}/location`, body).then((r) => r.data);

export const getDocuments = (id) =>
  API.get(`${base(id)}/documents`).then((r) => r.data);

export const addDocument = (id, body) =>
  API.post(`${base(id)}/documents`, body).then((r) => r.data);

export const removeDocument = (id, docId) =>
  API.delete(`${base(id)}/documents/${encodeURIComponent(docId)}`).then(
    (r) => r.data
  );

export const getActivity = (id, params) =>
  API.get(`${base(id)}/activity${qs(params)}`).then((r) => r.data);

/**
 * The pharmacy's wallet, straight off the ledger — the same figures its own
 * app shows. `cursor` pages the statement: the ledger is append-only and
 * ordered by an account-local sequence, so it is cursor-paginated rather than
 * offset-paginated (no skip, no count, and no rows shifting under you when a
 * new posting lands mid-read).
 */
export const getWallet = (id, { limit, cursor } = {}) =>
  API.get(`${base(id)}/wallet${qs({ limit, cursor })}`).then((r) => r.data);

/**
 * This pharmacy's withdrawal requests.
 *
 * Served by the payouts router rather than the pharmacy console's own, so
 * there is one definition of a payout request and the console cannot drift
 * from the payouts screen that acts on them.
 */
export const getPayoutRequests = (id, params) =>
  API.get(`/admin/payouts${qs({ pharmacyId: id, ...params })}`).then(
    (r) => r.data
  );
